import { readData, UploadStatus } from "@/api/data";
import { UploadType } from "@/api/types";
import Table, { Column, TableProps } from "@/components/Table/Table";
import useTableState from "@/components/Table/hooks/useTableState";
import TableHeader from "@/components/TableHeader/TableHeader";
import useGetResult from "@/hooks/useGetResults";
import { searchAndFilterToRootApiFilter, sortToRootSort } from "@/utilities/api";
import { FileAddOutlined, SaveOutlined, StopOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Col, Row, Space, Tooltip } from "antd";
import StatusTag from "../StatusTag/StatusTag";
import { DataStatsTags } from "../StatusTags/StatusTags";
import ProgressBar from "./ProgressBar";
import useValidationMutations from "./useValidationMutations";

const UploadStatusTranslation: Record<UploadStatus, string> = {
  pending: "Waiting for your upload to be picked up...",
  loading_data: "Unpacking your data...",
  validating_data: "Looking for gaps or errors...",
  uploading_validation_result: "Looking for gaps or errors...",
  failed: "Validation failed, please try uploading again",
  success: "Validation complete",
  importing_data: "Uploading your data..." /* should not be used in this step! */,
};

export interface UploadParameters {
  locationName?: string;
  productId?: string;
  departure?: string;
  arrival?: string;
  byTransportCategory?: boolean;
  facilityId?: number;
  year?: number;
}

export interface UploadDataTableProps extends TableProps {
  uploadParameters?: UploadParameters;
  uploadUUID: string;
  userFileName: string;
  uploadType: UploadType;
  onCancelFn: () => void;
  onImportFn: (importUUID: string) => void;
  productIds?: string[];
  year?: number;
  enableExpand?: boolean;
  preparingImport?: boolean;
  fileName: string;
}

export default function ValidationStep({
  fileName,
  uploadUUID,
  userFileName,
  uploadParameters,
  uploadType,
  onCancelFn,
  onImportFn,
  columns: parentColumns,
  productIds,
  enableExpand,
  year,
  availableFilters,
  preparingImport,
  ...props
}: UploadDataTableProps) {
  const { pagination, search, filter, sort, onSearchFilterSortPaginate } = useTableState({});

  const columns: Column[] = [
    ...parentColumns,
    {
      label: "Status",
      key: "status",
      filterable: true,
      renderer: (record) => {
        return (
          <StatusTag
            status={record.status}
            showIcon={false}
          />
        );
      },
    },
  ];

  const {
    UUID,
    isLoading: validationIsLoading,
    cancelValidation,
    progress,
    status,
  } = useValidationMutations({ parentPendingUUID: uploadUUID });

  const { data, isFetching: dataIsLoading } = useQuery({
    queryFn: () =>
      readData({
        UUID: UUID || "",
        ...pagination,
        activeFilters: searchAndFilterToRootApiFilter({ filter, search, columns }),
        activeSort: sortToRootSort(sort),
      }),
    enabled: !!UUID,
    queryKey: [uploadType, uploadParameters, UUID, pagination, search, filter, sort],
  });

  let { totalRows, dataSource, hasErrors, uploadBlocked } = useGetResult(data);

  const onSaveClick = async () => {
    UUID && onImportFn(UUID);
  };

  const onCancelClick = () => {
    cancelValidation();
    onCancelFn();
  };

  const isLoading = validationIsLoading || dataIsLoading;

  return (
    <>
      {status !== "success" && (
        <ProgressBar
          status={status}
          progress={progress}
          uploadStatusTranslation={UploadStatusTranslation}
        />
      )}
      {status === "success" && (
        <>
          <Space
            style={{ borderRadius: 8, background: "#fff", padding: "8px", marginBottom: "8px" }}
          >
            <FileAddOutlined />
            <strong>File</strong>
            {fileName}
          </Space>
          <TableHeader
            left={
              <Space.Compact>
                <Tooltip
                  trigger={"hover"}
                  title={
                    hasErrors || uploadBlocked
                      ? "Your data contains errors that need to be addressed before you can save"
                      : ""
                  }
                >
                  <Button
                    type="primary"
                    onClick={onSaveClick}
                    disabled={isLoading || hasErrors || uploadBlocked}
                    loading={isLoading || preparingImport}
                    icon={<SaveOutlined />}
                  >
                    {hasErrors && !uploadBlocked ? "Import and fix later" : "Import"}
                  </Button>
                </Tooltip>
                <Button
                  onClick={onCancelClick}
                  disabled={isLoading}
                  icon={<StopOutlined />}
                >
                  Cancel
                </Button>
              </Space.Compact>
            }
            right={
              <DataStatsTags
                data={data}
                loading={isLoading}
              />
            }
          />
          <Row>
            <Col span={24}>
              <Table
                data={dataSource}
                columns={columns}
                loading={isLoading}
                filter={filter}
                search={search}
                sort={sort}
                pagination={pagination}
                totalRows={totalRows}
                onSearchFilterSortPaginate={onSearchFilterSortPaginate}
                {...props}
                rowKey="key"
                availableFilters={{
                  ...availableFilters,
                  status: [
                    ...(data?.total_correct ? [{ label: "Correct", value: "correct" }] : []),
                    ...(data?.total_errors ? [{ label: "Error", value: "error" }] : []),
                    ...(data?.total_notes ? [{ label: "Note", value: "note" }] : []),
                    ...(data?.total_ignored ? [{ label: "Ignored", value: "ignored" }] : []),
                  ],
                }}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
